// Data Normalization Utilities
// This module handles combining data with the same hierarchy paths

import { SalesHierarchyNode, HierarchyLevel, SalesDataPoint } from '../types'

/**
 * Semantic path: `level:name` segments only (ignores opportunity suffixes).
 * Used to compare whether two rows represent the same hierarchy slot (for duplicate detection).
 */
export function hierarchySemanticPathKey(node: SalesHierarchyNode, allNodes: SalesHierarchyNode[]): string {
  const path: string[] = []

  function buildPath(currentNode: any, targetId: string): boolean {
    if (currentNode.id === targetId) {
      path.push(`${currentNode.level}:${currentNode.name}`)
      return true
    }

    if (currentNode.children) {
      for (const child of currentNode.children) {
        if (buildPath(child, targetId)) {
          path.push(`${currentNode.level}:${currentNode.name}`)
          return true
        }
      }
    }
    return false
  }

  for (const rootNode of allNodes) {
    if (buildPath(rootNode, node.id)) {
      break
    }
  }

  return path.reverse().join('|')
}

/**
 * Creates a unique key for a hierarchy path based on the node's position in the tree
 */
function createHierarchyKey(node: SalesHierarchyNode, allNodes: SalesHierarchyNode[]): string {
  const path: string[] = []
  
  // Build path from root to this node
  function buildPath(currentNode: any, targetId: string): boolean {
    if (currentNode.id === targetId) {
      const opportunityId = currentNode.metadata?.opportunity?.id
      path.push(`${currentNode.level}:${currentNode.name}${opportunityId ? `:opportunity:${opportunityId}` : ""}`)
      return true
    }
    
    if (currentNode.children) {
      for (const child of currentNode.children) {
        if (buildPath(child, targetId)) {
          const opportunityId = currentNode.metadata?.opportunity?.id
          path.push(`${currentNode.level}:${currentNode.name}${opportunityId ? `:opportunity:${opportunityId}` : ""}`)
          return true
        }
      }
    }
    return false
  }
  
  // Find the root node and build path
  for (const rootNode of allNodes) {
    if (buildPath(rootNode, node.id)) {
      break
    }
  }
  
  return path.reverse().join('|')
}

/**
 * Combines sales data from multiple data points for the same period
 */
function combineSalesData(dataPoints: SalesDataPoint[]): SalesDataPoint[] {
  const combinedData: { [periodKey: string]: SalesDataPoint } = {}

  dataPoints.forEach(dataPoint => {
    const periodKey = dataPoint.period.period_key

    if (combinedData[periodKey]) {
      // Combine metrics
      combinedData[periodKey].metrics.store_count += dataPoint.metrics.store_count
      combinedData[periodKey].metrics.volume_dollars += dataPoint.metrics.volume_dollars
      combinedData[periodKey].metrics.cases_sold += dataPoint.metrics.cases_sold

      // Preserve forecast/actual flags (if any are forecast, mark as forecast)
      if (dataPoint.is_forecast) {
        combinedData[periodKey].is_forecast = true
      }
      if (dataPoint.is_actual) {
        combinedData[periodKey].is_actual = true
      }
    } else {
      // Deep-copy metrics: the accumulation above must never mutate the caller's
      // data points (they live in component state; in-place += compounds totals).
      combinedData[periodKey] = { ...dataPoint, metrics: { ...dataPoint.metrics } }
    }
  })

  return Object.values(combinedData).sort((a, b) =>
    a.period.period_key.localeCompare(b.period.period_key)
  )
}

/**
 * Recursively normalizes a hierarchy node by combining data with the same path
 */
function normalizeNode(node: SalesHierarchyNode, allNodes: SalesHierarchyNode[]): SalesHierarchyNode {
  const normalizedNode = { ...node } as any
  
  // Normalize children first
  if (node.children && node.children.length > 0) {
    const childMap = new Map<string, any>()
    
    node.children.forEach(child => {
      const normalizedChild = normalizeNode(child as SalesHierarchyNode, allNodes)
      const childKey = createHierarchyKey(normalizedChild, allNodes)
      
      if (childMap.has(childKey)) {
        // Combine with existing child
        const existingChild = childMap.get(childKey)!
        
        // Ensure sales_data is an array before combining
        const existingChildSalesData = Array.isArray(existingChild.sales_data) ? existingChild.sales_data : []
        const normalizedChildSalesData = Array.isArray(normalizedChild.sales_data) ? normalizedChild.sales_data : []
        
        existingChild.sales_data = combineSalesData([
          ...existingChildSalesData,
          ...normalizedChildSalesData
        ])
        
        // Merge children if both have them
        if (normalizedChild.children && normalizedChild.children.length > 0) {
          if (!existingChild.children) {
            existingChild.children = []
          }
          existingChild.children.push(...normalizedChild.children)
        }
        
        // Update forecast config if not present
        if (!existingChild.forecast_config && normalizedChild.forecast_config) {
          existingChild.forecast_config = normalizedChild.forecast_config
        }
        if (!existingChild.metadata && normalizedChild.metadata) {
          existingChild.metadata = normalizedChild.metadata
        }
        
        // Update case cost if not present (only for product lines and SKUs)
        if ((existingChild as any).case_cost === undefined && (normalizedChild as any).case_cost !== undefined) {
          (existingChild as any).case_cost = (normalizedChild as any).case_cost
        }
        
        // Update SKU-specific fields if not present
        if (normalizedChild.level === HierarchyLevel.SKU) {
          if (!existingChild.product_sku && normalizedChild.product_sku) {
            existingChild.product_sku = normalizedChild.product_sku
          }
          if (!existingChild.sku_name && normalizedChild.sku_name) {
            existingChild.sku_name = normalizedChild.sku_name
          }
        }
      } else {
        childMap.set(childKey, normalizedChild)
      }
    })
    
    normalizedNode.children = Array.from(childMap.values())
  }
  
  // Ensure sales_data is always an array
  if (!Array.isArray(normalizedNode.sales_data)) {
    normalizedNode.sales_data = []
  }
  
  return normalizedNode as SalesHierarchyNode
}

/**
 * Normalizes an array of hierarchy nodes by combining data with the same hierarchy paths
 */
export function normalizeHierarchyData(nodes: SalesHierarchyNode[]): SalesHierarchyNode[] {
  const nodeMap = new Map<string, any>()
  
  // First pass: normalize each node and group by hierarchy key
  nodes.forEach(node => {
    const normalizedNode = normalizeNode(node, nodes)
    const hierarchyKey = createHierarchyKey(normalizedNode, nodes)
    
    if (nodeMap.has(hierarchyKey)) {
      // Combine with existing node
      const existingNode = nodeMap.get(hierarchyKey)!
      
      // Ensure sales_data is an array before combining
      const existingSalesData = Array.isArray(existingNode.sales_data) ? existingNode.sales_data : []
      const normalizedSalesData = Array.isArray(normalizedNode.sales_data) ? normalizedNode.sales_data : []
      
      existingNode.sales_data = combineSalesData([
        ...existingSalesData,
        ...normalizedSalesData
      ])
      
      // Merge children if both have them
      if (normalizedNode.children && normalizedNode.children.length > 0) {
        if (!existingNode.children) {
          existingNode.children = []
        }
        existingNode.children.push(...normalizedNode.children)
      }
      
      // Update forecast config if not present
      if (!existingNode.forecast_config && normalizedNode.forecast_config) {
        existingNode.forecast_config = normalizedNode.forecast_config
      }
      if (!existingNode.metadata && normalizedNode.metadata) {
        existingNode.metadata = normalizedNode.metadata
      }
    } else {
      nodeMap.set(hierarchyKey, normalizedNode)
    }
  })
  
  // Second pass: normalize children of each root node
  const normalizedNodes = Array.from(nodeMap.values()).map(node => {
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: normalizeHierarchyData(node.children)
      }
    }
    return node
  })
  
  return normalizedNodes as SalesHierarchyNode[]
}

/**
 * Creates a hierarchy path string for debugging/logging
 */
export function getHierarchyPath(node: SalesHierarchyNode, allNodes: SalesHierarchyNode[]): string {
  return createHierarchyKey(node, allNodes)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function make_node(id: string, name: string, volume: number): SalesHierarchyNode {
    return {
      id,
      name,
      level: "customer",
      parent_id: null,
      sales_data: [
        {
          period: { year: 2026, month: 1, period_key: "2026-01" },
          metrics: { store_count: 10, volume_dollars: volume, cases_sold: 5, case_cost: 10 },
        },
      ],
      children: [],
    } as unknown as SalesHierarchyNode
  }

  describe("normalizeHierarchyData", () => {
    it("combines duplicate hierarchy paths by summing metrics", () => {
      const merged = normalizeHierarchyData([make_node("a", "KeHE", 100), make_node("b", "KeHE", 50)])
      expect(merged).toHaveLength(1)
      expect(merged[0].sales_data[0].metrics.volume_dollars).toBe(150)
    })

    it("never mutates the input nodes when combining duplicates", () => {
      const first = make_node("a", "KeHE", 100)
      const second = make_node("b", "KeHE", 50)
      normalizeHierarchyData([first, second])
      normalizeHierarchyData([first, second])
      // In-place accumulation used to compound the first node's metrics on every pass.
      expect(first.sales_data[0].metrics.volume_dollars).toBe(100)
      expect(second.sales_data[0].metrics.volume_dollars).toBe(50)
    })
  })
}
